// src/features/remoteAssist/components/SupporterControlView.tsx

import { useEffect, useRef, useState } from "react";
import type { DomPatchPayload, RemoteAssistChannelMessage } from "../types";
import { useRemoteAssistChannelStore } from "../store/remoteAssistStore";
import { remoteAssistWebSocketUrl } from "../api";
import { toRelativeCoordinates } from "../domMirror/capture";
import { useEndRemoteAssistSession } from "../api";

interface SupporterControlViewProps {
  sessionId: string;
  vendorToken?: string; // فقط برای requester_scope=platform_vendor
}

export function SupporterControlView({ sessionId, vendorToken }: SupporterControlViewProps) {
  const [mirroredHtml, setMirroredHtml] = useState<string>("");
  const containerRef = useRef<HTMLDivElement>(null);
  const { connect, send, disconnect, connectionStatus, peerStatus, closedReason } = useRemoteAssistChannelStore();
  const { mutate: endSession, isPending } = useEndRemoteAssistSession();

  useEffect(() => {
    const role = vendorToken ? "vendor_supporter" : "supporter";
    connect({
      sessionId,
      role,
      url: remoteAssistWebSocketUrl(sessionId, vendorToken),
      onMessage: (message: RemoteAssistChannelMessage) => {
        if (message.type === "dom_patch") {
          setMirroredHtml((message.payload as DomPatchPayload).html);
        }
      },
    });
    return () => disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, vendorToken]);

  // Generate a reasonably unique CSS path for the element
  const getCssPath = (el: HTMLElement | null): string => {
    if (!el || el.nodeType !== Node.ELEMENT_NODE) return "";
    if (el.id) return `#${el.id}`;
    let path = el.tagName.toLowerCase();
    if (el.className && typeof el.className === "string") {
      path += el.className.split(/\s+/).filter(Boolean).map((c) => `.${c}`).join("");
    }
    const parent = el.parentElement;
    if (parent) {
      const siblings = Array.from(parent.children).filter((child) => child.tagName === el.tagName);
      if (siblings.length > 1) {
        const index = siblings.indexOf(el) + 1;
        path += `:nth-of-type(${index})`;
      }
      return `${getCssPath(parent)} > ${path}`;
    }
    return path;
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const { relativeX, relativeY } = toRelativeCoordinates(
      event.clientX - rect.left,
      event.clientY - rect.top,
      { width: rect.width, height: rect.height }
    );
    const target = event.target as HTMLElement;
    send({
      type: "input_event",
      payload: {
        kind: "click",
        relativeX,
        relativeY,
        targetSelector: getCssPath(target),
      },
    });
  };

  const handleInput = (event: React.FormEvent<HTMLDivElement>) => {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    send({
      type: "input_event",
      payload: {
        kind: "input",
        targetSelector: getCssPath(target),
        value: target.value,
      },
    });
  };

  return (
    <div
      dir="rtl"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        minHeight: "calc(100vh - 120px)",
        padding: "0.5rem 0",
        fontFamily: "var(--font-family-fa, sans-serif)",
      }}
    >
      {/* TOOLBAR */}
      <div
        className="brand-card-extand"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 18px",
          color: "var(--color-text-main, #2B2B2B)",
          fontSize: "0.85rem",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <span
            style={{
              padding: "4px 12px",
              borderRadius: "8px",
              fontWeight: 700,
              fontSize: "0.8rem",
              background: connectionStatus === "open" ? "rgba(16, 185, 129, 0.15)" : "rgba(245, 158, 11, 0.15)",
              color: connectionStatus === "open" ? "#065F46" : "#B45309",
            }}
          >
            {connectionStatus === "open" ? "🟢 اتصال زنده برقرار است" : `وضعیت: ${connectionStatus}`}
          </span>
          <span style={{ fontWeight: 600, color: "var(--color-text-muted, #666)" }}>
            حالت تعامل: کنترل کامل
          </span>
        </div>

        {closedReason && (
          <div
            data-testid="supporter-session-ended-notice"
            style={{
              color: "#FFFFFF",
              fontWeight: 700,
              padding: "6px 14px",
              backgroundColor:
                closedReason === "revoked"
                  ? "#991B1B"
                  : closedReason === "connection_lost"
                    ? "#B45309"
                    : "#4B5563",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
              fontSize: "0.85rem",
            }}
          >
            {closedReason === "connection_lost" && "ارتباط با کاربر به دلیل قطعی طولانی پایان یافت."}
            {closedReason === "revoked" && "دسترسی شما به این سشن لغو شد و سشن پایان یافت."}
            {closedReason === "ended" && "سشن ریموت اسیست پایان یافته است."}
            {closedReason === "forbidden" && "دسترسی به این سشن مجاز نیست."}
          </div>
        )}

        {!closedReason && peerStatus === "peer_disconnected" && (
          <div
            style={{
              color: "#B91C1C",
              fontWeight: 700,
              padding: "6px 14px",
              background: "rgba(239, 68, 68, 0.12)",
              border: "1px solid #EF4444",
              borderRadius: "8px",
              fontSize: "0.85rem",
            }}
          >
            ⚠️ ارتباط با کاربر قطع شد — منتظر وصل مجدد... (حداکثر ۵ دقیقه مهلت)
          </div>
        )}

        {!closedReason && peerStatus === "peer_reconnected" && (
          <div
            style={{
              color: "#1D4ED8",
              fontWeight: 700,
              padding: "6px 14px",
              background: "rgba(59, 130, 246, 0.12)",
              border: "1px solid #3B82F6",
              borderRadius: "8px",
              fontSize: "0.85rem",
            }}
          >
            ✓ ارتباط دوباره برقرار شد.
          </div>
        )}

        <button
          type="button"
          onClick={() => endSession(sessionId)}
          disabled={isPending}
          style={{
            backgroundColor: "#DC2626",
            color: "#FFFFFF",
            padding: "8px 18px",
            fontSize: "0.85rem",
            fontWeight: 800,
            borderRadius: "10px",
            border: "none",
            cursor: isPending ? "not-allowed" : "pointer",
            boxShadow: "0 2px 8px rgba(220, 38, 38, 0.25)",
            transition: "background 0.2s ease",
          }}
        >
          {isPending ? "در حال قطع..." : "🛑 پایان اتصال"}
        </button>
      </div>

      {/* MIRRORED VIEW CONTAINER WITH CSS CONTAINMENT TO PREVENT STYLE LEAKS */}
      <div
        className="brand-card-extand"
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          padding: "0",
          overflow: "hidden",
          minHeight: "680px",
        }}
      >
        <div
          style={{
            padding: "8px 16px",
            borderBottom: "1px solid var(--color-border, #E4DCC8)",
            background: "rgba(0, 0, 0, 0.02)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B" }}>
            🖥️ صفحه کاربر (آینه زنده DOM)
          </span>
          <span style={{ fontSize: "0.75rem", color: "var(--color-text-muted, #666)" }}>
            کلیک و تایپ شما مستقیماً روی صفحه کاربر اعمال می‌شود
          </span>
        </div>

        <div
          ref={containerRef}
          onClick={handleClick}
          onInput={handleInput}
          onSubmit={(e) => e.preventDefault()}
          style={{
            flex: 1,
            overflow: "auto",
            backgroundColor: "#FFFFFF",
            padding: "16px",
            position: "relative",
            contain: "content", // ایزوله‌سازی محتوا برای جلوگیری قطعی از تأثیر روی سایدبار و استایل‌های اصلی
          }}
          dangerouslySetInnerHTML={{ __html: mirroredHtml || "<div style='padding: 3rem; text-align: center; color: #888;'>در انتظار دریافت تصویر صفحه کاربر...</div>" }}
        />
      </div>
    </div>
  );
}

export default SupporterControlView;
